import { useContext, useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { JobsStatus, jobStatusToString } from "../../../constants/Jobs";
import { FeatureTableContext } from "../../../Context";
import {
    getJobRelatedDocumentation,
    getJobRelatedObjects,
    hideDocumentationPoints,
    showDocumentationPoints,
} from "../../../utils/jobsUtils";
import NfsButton from "../../genericComponents/NfsButton/NfsButton";
import "./JobDetails.css";

import CheckPinIcon from "../../../assets/jobs/checkPin.svg?react";

import { formatDate } from "@arcgis/core/intl";
import { StoreState } from "../../../store/rootReducer";
import { getLayerName } from "../../../utils/arcgisUtils";
import { FeatureWithChanges, findFeaturesWithChangesFromJob } from "../../../utils/newVersioningUtils";
import EditedFeaturesForOperativeCompare from "../EditedFeatures/EditedFeaturesForOperativeCompare";
import Collapsible from "../../genericComponents/Collapsible/Collapsible";
import type Graphic from "@arcgis/core/Graphic";
import type { ResourceHandle } from "@arcgis/core/core/Handles";

interface JobReadOnlyDetailsProps {
    jobObject: Graphic;
    storeHighlightHandle: (handle: ResourceHandle | null) => void;
    clearHighlightedFeatures: () => void;
}

const JobReadOnlyDetails: React.FC<JobReadOnlyDetailsProps> = ({
    jobObject,
    storeHighlightHandle,
    clearHighlightedFeatures,
}) => {
    const [historicalSteps, setHistoricalSteps] = useState<Graphic[]>([]);

    const [jobFeaturesWithChanges, setJobFeaturesWithChanges] = useState<FeatureWithChanges[]>([]);
    const [isLoadingChanges, setIsLoadingChanges] = useState<boolean>(true);

    const [documentation, setDocumentation] = useState<Graphic[]>([]);
    const [selectedDocumentation, setSelectedDocumentation] = useState<Graphic | null>(null);

    const activeVersion = useSelector((state: StoreState) => state.version.versionInfo);

    const featureTableContext = useContext(FeatureTableContext);

    const getObjectsConnectedToJob = async () => {
        const job = jobObject;
        const versionName = activeVersion?.versionIdentifier.name;

        if (job && versionName) {
            setIsLoadingChanges(true);
            const objects = await findFeaturesWithChangesFromJob(job, versionName);
            setJobFeaturesWithChanges(objects);
            setIsLoadingChanges(false);
        }
    };

    const getDocumentationSeamarkNumber = (graphic: Graphic) => {
        const documentationRelatedObjectGlobalId = graphic.attributes.nautisk_objekt_globalid;
        const documentationRelatedLayerId = graphic.attributes.nautisk_objekt_kilde;
        let documentationRelatedFeature: Graphic | undefined;
        for (let featureWithChange of jobFeaturesWithChanges) {
            if (
                featureWithChange.mainFeature.layerId === documentationRelatedLayerId &&
                featureWithChange.mainFeature.versionGraphic?.attributes.globalid === documentationRelatedObjectGlobalId
            ) {
                documentationRelatedFeature = featureWithChange.mainFeature.versionGraphic;
                break;
            }
            for (let relatedFeature of featureWithChange.relatedFeatures) {
                if (
                    relatedFeature.layerId === documentationRelatedLayerId &&
                    relatedFeature.versionGraphic?.attributes.globalid === documentationRelatedObjectGlobalId
                ) {
                    documentationRelatedFeature = relatedFeature.versionGraphic;
                    break;
                }
            }
            if (documentationRelatedFeature) {
                break;
            }
        }

        return (
            documentationRelatedFeature?.attributes["sjomerke_nr"] ??
            documentationRelatedFeature?.attributes["navn"] ??
            documentationRelatedFeature?.attributes["objectid"] + getLayerName(documentationRelatedLayerId)
        );
    };

    useEffect(() => {
        getJobRelatedObjects(jobObject.attributes.objectid, "Steg").then((objects) => {
            // Find step before this step
            const orderedSteps = [...objects];
            // Sorts steps by opprettet_dato in descending order in case there are multiple steps with the same jobb_status
            orderedSteps.sort((a, b) => b.attributes.opprettet_dato - a.attributes.opprettet_dato);
            let activeStep;
            for (let step of orderedSteps) {
                if (step.attributes.jobb_status === jobObject.attributes.status) {
                    activeStep = step;
                    break;
                }
            }
            if (activeStep) {
                setHistoricalSteps(() => {
                    if (jobObject.attributes.status === JobsStatus.Ferdig) {
                        return orderedSteps;
                    } else {
                        return orderedSteps.filter(
                            (step) => step.attributes.globalid !== activeStep.attributes.globalid
                        );
                    }
                });
            }
        });
        getJobRelatedDocumentation(jobObject.attributes.objectid).then((docs) => {
            setDocumentation(docs);
        });
    }, [jobObject]);

    useEffect(() => {
        if (selectedDocumentation) {
            showDocumentationPoints(selectedDocumentation).then((layer) => {
                if (layer) {
                    if (!layer.loaded) {
                        layer.load().then(() => {
                            featureTableContext?.isOpen.set(true);
                            featureTableContext?.activeLayer.set(layer);
                        });
                    } else {
                        featureTableContext?.isOpen.set(true);
                        featureTableContext?.activeLayer.set(layer);
                    }
                }
            });
        }
    }, [selectedDocumentation]);

    useEffect(() => {
        getObjectsConnectedToJob();
    }, []);

    useEffect(() => {
        return () => {
            featureTableContext?.activeLayer.set(undefined);
            featureTableContext?.isOpen.set(false);
            hideDocumentationPoints();
        };
    }, []);

    return (
        <div className="job-details-body">
            <div className="job-details-body-content">
                <label className="job-details-label">
                    <span className="hidden">{jobObject.attributes.globalid}</span>
                    <span>Jobbnavn</span>
                    <span>{jobObject.attributes.navn}</span>
                </label>
                <label className="job-details-label">
                    <span>Beskrivelse</span>
                    <span>{jobObject.attributes.beskrivelse}</span>
                </label>
                {historicalSteps.length > 0 && (
                    <Collapsible
                        isOpen={true}
                        setIsOpen={() => {}}
                        title={`Steg med kommentarer (${historicalSteps.length})`}
                        titleClassName="input-field-label"
                    >
                        {historicalSteps.map((step) => (
                            <label key={step.attributes.globalid} className="job-step-container">
                                <div className="job-step-header">
                                    <span>
                                        <strong>{jobStatusToString(step.attributes.jobb_status)}</strong>
                                    </span>
                                    <span>
                                        {step.attributes.fullfort_av} ({formatDate(step.attributes.oppdatert_dato)})
                                    </span>
                                </div>
                                <span>{step.attributes.kommentar ?? "[ingen kommentar]"}</span>
                            </label>
                        ))}
                    </Collapsible>
                )}

                <div className="empty-button flex-row small-gap center">
                    <CheckPinIcon />
                    {`Objekter tilknyttet jobben (${jobFeaturesWithChanges.length})`}
                </div>
                <div className="flex-row space-between">
                    <span className="job-card-key">Eier</span>
                    <span className="job-card-value bold">{jobObject.attributes.opprettet_av}</span>
                </div>
                <div className="flex-row space-between">
                    <span className="job-card-key">Oppdatert</span>
                    <span className="job-card-value bold">{formatDate(jobObject.attributes.oppdatert_dato)}</span>
                </div>
                {documentation.length > 0 && (
                    <Collapsible
                        isOpen={false}
                        setIsOpen={() => {}}
                        title={`Dokumentasjon (${documentation.length})`}
                        titleClassName="input-field-label"
                    >
                        {documentation.map((doc) => {
                            return (
                                <div key={doc.attributes.globalid} className="job-details-documentation-item">
                                    <NfsButton
                                        outlined
                                        className={`w-100 job-details-documentation-button ${
                                            doc === selectedDocumentation ? "selected-documentation" : ""
                                        }`}
                                        onClick={() => {
                                            setSelectedDocumentation(doc);
                                        }}
                                    >
                                        <span>
                                            {getDocumentationSeamarkNumber(doc) +
                                                " " +
                                                (doc.attributes.forklaring ?? doc.attributes.beskrivelse ?? "")}
                                        </span>
                                    </NfsButton>
                                </div>
                            );
                        })}
                    </Collapsible>
                )}
            </div>
            <EditedFeaturesForOperativeCompare
                featuresAddedToJob={jobFeaturesWithChanges}
                isLoadingChanges={isLoadingChanges}
                storeHighlightHandle={storeHighlightHandle}
                clearHighlightedFeatures={clearHighlightedFeatures}
                jobStatus={jobObject.attributes.status}
            />
        </div>
    );
};

export default JobReadOnlyDetails;
